import { useMissingTranslationCheck } from '@src/hooks/useMissingTranslationCheck';
import { useUpdateSearchParams } from '@src/hooks/useUpdateSearchParams';
import {
  getLastStoryLanguage,
  recordLastStoryLanguage,
} from '@src/utility/lastStoryLanguage';
import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

interface RestoreClaim {
  storyId: string;
  /** The remembered language being restored; `null` = nothing to restore. */
  language: string | null;
  landed: boolean;
}

/**
 * PLAT-11898 — Creator Studio opened without a `lang` (sidebar, story list,
 * Content Studio) resumes on the language this user last worked in on the
 * story. The language is put in the URL BEFORE the studio renders, so every
 * `?lang=` reader sees it on its first render, exactly as on a reload with
 * `?lang=`; restoring from inside the studio raced its own mount-time URL
 * syncs and lost.
 *
 * Optimistic: the studio is not held for validation. The language is checked
 * against the story's translations in the background, and one the story no
 * longer has is dropped from the URL and forgotten.
 */
export const CreatorStudioLanguageGate: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const storyId = searchParams.get('storyId');
  const urlLanguage = searchParams.get('lang');

  // Once per story per mount — afterwards `lang` is the studio's to change.
  const claimRef = useRef<RestoreClaim | null>(null);
  if (storyId && claimRef.current?.storyId !== storyId) {
    claimRef.current = {
      storyId,
      language: urlLanguage ? null : getLastStoryLanguage(storyId),
      landed: false,
    };
  }
  const claim = claimRef.current;
  if (claim?.language && urlLanguage === claim.language) claim.landed = true;
  const redirecting = !!claim?.language && !claim.landed;

  // A story switch inside the studio redirects alongside the mounted studio
  // rather than unmounting it; only the first mount waits for the URL.
  const childrenMountedRef = useRef(false);
  useEffect(() => {
    if (!redirecting) childrenMountedRef.current = true;
  });

  const updateSearchParams = useUpdateSearchParams();
  const restoredStoryId = claim?.language ? claim.storyId : null;
  const restoredLanguage = claim?.language ?? null;
  useMissingTranslationCheck(restoredStoryId, restoredLanguage, () => {
    if (!restoredStoryId || !restoredLanguage) return;
    // Gone from the story. A pick made since then is left alone.
    if (getLastStoryLanguage(restoredStoryId) === restoredLanguage) {
      recordLastStoryLanguage(restoredStoryId, null);
    }
    const live = new URLSearchParams(window.location.search);
    if (live.get('lang') === restoredLanguage) {
      updateSearchParams(params => params.delete('lang'));
    }
  });

  const redirectSearch = new URLSearchParams(searchParams);
  if (redirecting && claim?.language)
    redirectSearch.set('lang', claim.language);

  return (
    <>
      {redirecting && (
        <Navigate
          replace
          to={{ pathname, search: redirectSearch.toString() }}
        />
      )}
      {(!redirecting || childrenMountedRef.current) && children}
    </>
  );
};
